import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { mangaList, chapterData, articleContent, categories } from '@/mocks/mangaData';

function parseArticleId(articleId: string): { mangaId: string; chapterId: string } | null {
  const lastDash = articleId.lastIndexOf('-');
  if (lastDash === -1) return null;
  const mangaId = articleId.substring(0, lastDash);
  const chapterId = articleId;
  return { mangaId, chapterId };
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  if (!id) return null;

  const parsed = parseArticleId(id);
  if (!parsed) {
    navigate('/', { replace: true });
    return null;
  }

  const { mangaId, chapterId } = parsed;
  const manga = mangaList.find((m) => m.id === mangaId);
  const chapters = chapterData[mangaId] || [];
  const article = articleContent[chapterId];

  if (!manga || !article) {
    // Find closest match
    const anyChapter = chapters[0];
    if (anyChapter) {
      navigate(`/article/${anyChapter.id}`, { replace: true });
      return null;
    }
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">章节未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">该章节不存在或已被移除</p>
            <Link to="/" className="px-6 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const chapterIndex = chapters.findIndex((ch) => ch.id === chapterId);
  const prevChapter = chapterIndex < chapters.length - 1 ? chapters[chapterIndex + 1] : null;
  const nextChapter = chapterIndex > 0 ? chapters[chapterIndex - 1] : null;

  const currentChapter = chapters[chapterIndex];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - ${article.title}`,
    description: `${manga.title}${article.title}，36漫画网为您提供在线免费阅读，全集无删减，高清画质。`,
    author: {
      '@type': 'Person',
      name: manga.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '36漫画网',
    },
    datePublished: currentChapter?.updatedAt,
    dateModified: currentChapter?.updatedAt,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://36manhua.com/article/${chapterId}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <Navbar />

      <main className="pt-20 pb-10">
        <div className="px-4 md:px-6 max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: manga.category ? `${categories.find(c => c.id === manga.category)?.name || '漫画'}` : '漫画', href: `/category/${manga.category}` },
              { label: manga.title, href: `/article/${mangaId}-ch${manga.chapterCount}` },
              { label: article.title },
            ]}
          />

          {/* Chapter Header */}
          <div className="mt-8 mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              {manga.title}
            </h1>
            <p className="mt-2 text-lg font-heading font-semibold text-foreground-700">
              {article.title}
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-3">
              <span className="text-sm text-foreground-500">
                <i className="ri-user-line mr-1"></i> {manga.author}
              </span>
              <span className="text-sm text-foreground-500">
                <i className="ri-pages-line mr-1"></i> {currentChapter?.pageCount || '?'}页
              </span>
              <span className="text-sm text-foreground-500">
                <i className="ri-time-line mr-1"></i> {currentChapter?.updatedAt || ''}
              </span>
              <span className="px-2 py-0.5 rounded-md bg-primary-100 text-primary-700 text-xs font-medium">
                {manga.status}
              </span>
            </div>
          </div>

          {/* Chapter Navigation Top */}
          <div className="flex items-center justify-between py-3 border-y border-background-200/70 mb-8">
            {prevChapter ? (
              <Link
                to={`/article/${prevChapter.id}`}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background-100 text-sm text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> {prevChapter.title}
              </Link>
            ) : (
              <span className="px-4 py-2 text-sm text-foreground-400">已是最后一话</span>
            )}
            <Link
              to={`/article/${mangaId}-ch${manga.chapterCount}`}
              className="px-4 py-2 rounded-lg bg-background-100 text-sm text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-list-check mr-1"></i> 章节列表
            </Link>
            {nextChapter ? (
              <Link
                to={`/article/${nextChapter.id}`}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 text-white text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {nextChapter.title} <i className="ri-arrow-right-line"></i>
              </Link>
            ) : (
              <span className="px-4 py-2 text-sm text-foreground-400">已是最新一话</span>
            )}
          </div>

          {/* Article Content */}
          <article className="prose-custom">
            <div className="bg-background-100 rounded-xl p-6 md:p-10">
              {article.content.split('\n\n').map((paragraph, i) => (
                <p
                  key={i}
                  className={`text-foreground-800 leading-loose ${
                    i > 0 ? 'mt-4' : ''
                  }`}
                >
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Simulated manga pages */}
            <div className="mt-6 space-y-6">
              <div className="relative rounded-xl overflow-hidden bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Jujutsu%20Kaisen%20manga%20page%20style%20black%20and%20white%20Japanese%20comic%20panel%20layout%2C%20dynamic%20action%20sequence%2C%20speed%20lines%2C%20dramatic%20shading%2C%20clean%20manga%20artwork&width=1200&height=900&seq=manga-page-p1&orientation=landscape"
                  alt={`${manga.title} ${article.title} 第1页`}
                  className="w-full h-auto"
                  title={`${manga.title} - ${article.title} 第1页`}
                  loading="lazy"
                />
                <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md bg-black/50 text-white text-xs">1</div>
              </div>
              <div className="relative rounded-xl overflow-hidden bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Jujutsu%20Kaisen%20manga%20page%20style%20black%20and%20white%20Japanese%20comic%20panel%20layout%2C%20dynamic%20action%20sequence%2C%20speed%20lines%2C%20dramatic%20shading%2C%20clean%20manga%20artwork&width=1200&height=900&seq=manga-page-p2&orientation=landscape"
                  alt={`${manga.title} ${article.title} 第2页`}
                  className="w-full h-auto"
                  title={`${manga.title} - ${article.title} 第2页`}
                  loading="lazy"
                />
                <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md bg-black/50 text-white text-xs">2</div>
              </div>
              <div className="relative rounded-xl overflow-hidden bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Jujutsu%20Kaisen%20manga%20page%20style%20black%20and%20white%20Japanese%20comic%20panel%20layout%2C%20dynamic%20action%20sequence%2C%20speed%20lines%2C%20dramatic%20shading%2C%20clean%20manga%20artwork&width=1200&height=900&seq=manga-page-p3&orientation=landscape"
                  alt={`${manga.title} ${article.title} 第3页`}
                  className="w-full h-auto"
                  title={`${manga.title} - ${article.title} 第3页`}
                  loading="lazy"
                />
                <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md bg-black/50 text-white text-xs">3</div>
              </div>
              <div className="relative rounded-xl overflow-hidden bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Jujutsu%20Kaisen%20manga%20page%20style%20black%20and%20white%20Japanese%20comic%20panel%20layout%2C%20dynamic%20action%20sequence%2C%20speed%20lines%2C%20dramatic%20shading%2C%20clean%20manga%20artwork&width=1200&height=900&seq=manga-page-p4&orientation=landscape"
                  alt={`${manga.title} ${article.title} 第4页`}
                  className="w-full h-auto"
                  title={`${manga.title} - ${article.title} 第4页`}
                  loading="lazy"
                />
                <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md bg-black/50 text-white text-xs">4</div>
              </div>
              <div className="relative rounded-xl overflow-hidden bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Jujutsu%20Kaisen%20manga%20page%20style%20black%20and%20white%20Japanese%20comic%20panel%20layout%2C%20dynamic%20action%20sequence%2C%20speed%20lines%2C%20dramatic%20shading%2C%20clean%20manga%20artwork&width=1200&height=900&seq=manga-page-p5&orientation=landscape"
                  alt={`${manga.title} ${article.title} 第5页`}
                  className="w-full h-auto"
                  title={`${manga.title} - ${article.title} 第5页`}
                  loading="lazy"
                />
                <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md bg-black/50 text-white text-xs">5</div>
              </div>
            </div>
          </article>

          {/* Chapter Navigation Bottom */}
          <div className="flex items-center justify-between py-3 border-y border-background-200/70 mt-10">
            {prevChapter ? (
              <Link
                to={`/article/${prevChapter.id}`}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background-100 text-sm text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> {prevChapter.title}
              </Link>
            ) : (
              <span className="px-4 py-2 text-sm text-foreground-400">已是最后一话</span>
            )}
            <Link
              to={`/article/${mangaId}-ch${manga.chapterCount}`}
              className="px-4 py-2 rounded-lg bg-background-100 text-sm text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-list-check mr-1"></i> 章节列表
            </Link>
            {nextChapter ? (
              <Link
                to={`/article/${nextChapter.id}`}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 text-white text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {nextChapter.title} <i className="ri-arrow-right-line"></i>
              </Link>
            ) : (
              <span className="px-4 py-2 text-sm text-foreground-400">已是最新一话</span>
            )}
          </div>

          {/* Chapter List */}
          <section className="mt-10">
            <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">
              章节列表
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {chapters.map((ch) => (
                <Link
                  key={ch.id}
                  to={`/article/${ch.id}`}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    ch.id === chapterId
                      ? 'bg-primary-100 text-primary-700 font-medium'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <span className="text-sm truncate">{ch.title}</span>
                  <span className="text-xs text-foreground-400 ml-2 shrink-0">{ch.updatedAt}</span>
                </Link>
              ))}
            </div>
          </section>
        </div>

        {/* Related Manga */}
        <section className="mt-16 py-14 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
              猜你喜欢
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
              {mangaList
                .filter((m) => m.category === manga.category && m.id !== manga.id)
                .slice(0, 6)
                .map((m) => (
                  <Link
                    key={m.id}
                    to={`/article/${m.id}-ch${m.chapterCount}`}
                    className="group block"
                  >
                    <div className="relative overflow-hidden rounded-lg bg-background-200 aspect-[3/4]">
                      <img
                        src={m.cover}
                        alt={m.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        title={m.title}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-2">
                        <h3 className="text-xs font-semibold text-white line-clamp-1">{m.title}</h3>
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}